import React, { useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import {
  Box,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  HStack
} from '@chakra-ui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { RentalApplication } from '@homevest/types/rental-applications'
import { RENTAL_APPLICATION_STATUSES } from '@homevest/utils/rental-applications'
import { UserUnderwritingData, SaferentRequest } from '@homevest/types/user-saferent'
import { SAFERENT_REQUEST_STATUSES, SAFERENT_REQUEST_TYPES } from '@homevest/utils/saferent'

import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import { formatDateWithTime } from 'lib/date-time'
import { formatMoney } from 'lib/numbers'
import { Link } from 'ui'

import EditSaferentDetailsModal from './EditSaferentDetailsModal'
import RereunRequestModal from './RereunRequestModal'

const UserInfoItem: React.FC<
  React.PropsWithChildren<{ label: string; value: string | number | null | React.ReactNode }>
> = ({ label, value }) => {
  return (
    <VStack alignSelf='flex-start' alignItems='flex-start'>
      {label && (
        <Text fontSize='sm' fontWeight='medium'>
          {label}
        </Text>
      )}
      <Box marginTop={0} fontSize='sm'>
        {value || null}
      </Box>
    </VStack>
  )
}

type UserWithData = {
  userId: string
  userName: string
  underwritingData: UserUnderwritingData | null
  saferentRequests: SaferentRequest[]
}

// Helper function to display the JSON blobs we get from SafeRent API
const formatJsonField = (value: any) => {
  const parsed = value ? JSON.parse(value) : []

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) {
      return 'N/A'
    }
    return (
      <>
        {parsed.map((item: Record<string, string>, index: number) => {
          const displayKey = Object.keys(item)[0]
          const alertValue = Object.values(item)[0]
          const keyWithoutAlert = displayKey.replace(/\s+Alert$/, '')
          const hasAlert = displayKey.includes('Alert')
          return (
            <Box key={index} fontSize='xs'>
              <Box display='flex' alignItems='center' whiteSpace='nowrap'>
                {hasAlert && (
                  <Box as='span' display='inline-flex' alignItems='center' mr={1}>
                    <ExclamationTriangleIcon style={{ width: '14px', height: '14px' }} />
                  </Box>
                )}
                <Text as='span' fontWeight='medium'>
                  {keyWithoutAlert}
                </Text>
              </Box>
              <Text as='span'>{alertValue}</Text>
            </Box>
          )
        })}
      </>
    )
  }

  if (typeof parsed === 'object') {
    if (isEmpty(parsed)) {
      return 'N/A'
    }
    return Object.entries(parsed).map(([key, value]: [string, any]) => {
      const keyWithoutAlert = key.replace(/\s+Alert$/, '')
      const hasAlert = key.includes('Alert')
      return (
        <Box key={key} fontSize='xs'>
          <Box display='flex' alignItems='center' whiteSpace='nowrap'>
            {hasAlert && (
              <Box as='span' display='inline-flex' alignItems='center' mr={1}>
                <ExclamationTriangleIcon style={{ width: '14px', height: '14px' }} />
              </Box>
            )}
            <Text as='span' fontWeight='medium'>
              {keyWithoutAlert}
            </Text>
          </Box>
          <Text as='span'>{value}</Text>
        </Box>
      )
    })
  }
  return parsed
}

const SaferentDetailsInner = ({
  rentalApplication
}: {
  rentalApplication: RentalApplication
}): JSX.Element => {
  const usersWithData = useMemo<UserWithData[]>(() => {
    const saferentRequests = rentalApplication.saferent_requests || []
    const userUnderwritingData = rentalApplication.user_underwriting_data || []
    const leadGroupUsers = rentalApplication.lead_group?.lead_group_users || []

    // Group data by user
    const userMap = new Map<string, UserWithData>()

    // Initialize users from lead_group_users
    leadGroupUsers.forEach((lgu) => {
      const userId = lgu.users.id
      const userName =
        `${lgu.users.first_name || ''} ${lgu.users.last_name || ''}`.trim() || 'Unknown User'

      if (!userMap.has(userId)) {
        userMap.set(userId, {
          userId,
          userName,
          underwritingData: null,
          saferentRequests: []
        })
      }
    })

    // Add underwriting data
    userUnderwritingData.forEach((data: UserUnderwritingData) => {
      const userData = userMap.get(data.user_id)
      if (userData) {
        userData.underwritingData = data
      }
    })

    // Add saferent requests
    saferentRequests.forEach((request) => {
      const userData = userMap.get(request.user_id)
      if (userData) {
        userData.saferentRequests.push(request)
      }
    })

    // Sort saferent requests by created_at (most recent first)
    userMap.forEach((userData) => {
      userData.saferentRequests.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    })

    return Array.from(userMap.values())
  }, [rentalApplication])

  const hasAnyData = usersWithData.length > 0

  return (
    <>
      {!hasAnyData ? (
        <Text>No Saferent data available</Text>
      ) : (
        <VStack align='stretch' spacing={2} width='100%'>
          {usersWithData.map((userData) => {
            return (
              <Box key={userData.userId} width='100%' mb={4}>
                <Text fontSize='xl' fontWeight='bold' mb={4}>
                  <Link
                    to={`/users/${userData.userId}`}
                    useRouter
                    showIcon
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {userData.userName}
                  </Link>
                </Text>

                {/* User Underwriting Details */}
                {userData.underwritingData && (
                  <VStack mt={4}>
                    <HStack
                      spacing={12}
                      justifyContent='space-between'
                      display='flex'
                      alignSelf='flex-start'
                    >
                      <UserInfoItem
                        label='Credit Score'
                        value={userData.underwritingData.credit_score}
                      />
                      <UserInfoItem
                        label='Verified Income'
                        value={
                          userData.underwritingData.verified_income != null
                            ? formatMoney(
                                Number(userData.underwritingData.verified_income),
                                2,
                                '$'
                              ) || null
                            : null
                        }
                      />
                      <UserInfoItem
                        label='Criminal Match'
                        value={formatJsonField(userData.underwritingData.criminal_match)}
                      />
                      {(!userData.underwritingData.alerts ||
                        isEmpty(userData.underwritingData.alerts)) && (
                        <UserInfoItem
                          label='Alerts'
                          value={formatJsonField(userData.underwritingData.alerts)}
                        />
                      )}
                    </HStack>
                    {userData.underwritingData.alerts &&
                      !isEmpty(userData.underwritingData.alerts) && (
                        <UserInfoItem
                          label=''
                          value={formatJsonField(userData.underwritingData.alerts)}
                        />
                      )}
                  </VStack>
                )}

                {/* Saferent Requests Table */}
                {userData.saferentRequests.length > 0 && (
                  <Box overflowX='auto' mt={4}>
                    <Table variant='simple' size='sm'>
                      <Thead>
                        <Tr>
                          <Th>Status</Th>
                          <Th>Type</Th>
                          <Th>Description</Th>
                          <Th>Notes</Th>
                          <Th>Triggered At</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {userData.saferentRequests.map((request: SaferentRequest) => (
                          <Tr key={request.id}>
                            <Td>{mapContentToBadge(request.status, { size: 'xs', dot: false })}</Td>
                            <Td>{mapContentToBadge(request.type, { size: 'xs', dot: false })}</Td>
                            <Td fontSize='xs'>
                              <Text maxW='200px' isTruncated>
                                {request.description}
                              </Text>
                            </Td>
                            <Td maxW='200px' fontSize='xs'>
                              {request.saferent_notes || 'N/A'}
                            </Td>
                            <Td>{formatDateWithTime(request.triggered_at)}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </Box>
            )
          })}
        </VStack>
      )}
    </>
  )
}

const SaferentDetails = ({
  rentalApplication,
  onRefresh
}: {
  rentalApplication: RentalApplication
  onRefresh?: () => void | Promise<void>
}) => {
  const [rerunInitialRequestModalOpen, setRerunInitialRequestModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [rerunFinalRequestModalOpen, setRerunFinalRequestModalOpen] = useState(false)

  // action button type
  let buttonType: 'initialRequest' | 'finalRequest' | 'edit' | null = null
  if (
    rentalApplication.status === RENTAL_APPLICATION_STATUSES.PENDING_INITIAL_UNDERWRITING_REQUEST
  ) {
    buttonType = 'initialRequest'
  } else if (
    rentalApplication.status === RENTAL_APPLICATION_STATUSES.PENDING_INITIAL_UNDERWRITING
  ) {
    buttonType = 'edit'
  } else if (
    rentalApplication.status === RENTAL_APPLICATION_STATUSES.PENDING_FINAL_UNDERWRITING_REQUEST
  ) {
    buttonType = 'finalRequest'
  }

  // can rerun if action button is to rerun requests
  let canRerun = false
  if (buttonType === 'initialRequest') {
    const leadGroupUsers = rentalApplication.lead_group?.lead_group_users || []
    const initialRequests =
      rentalApplication.saferent_requests?.filter(
        (request) => request.type === SAFERENT_REQUEST_TYPES.INITIAL
      ) || []

    // Check if any user has an error request
    canRerun = leadGroupUsers.some((lgu) => {
      const userRequests = initialRequests.filter((request) => request.user_id === lgu.users.id)
      if (userRequests.length === 0) {
        return true
      }
      // Check if user has at least one error request
      return userRequests.some(
        (request) =>
          request.status === SAFERENT_REQUEST_STATUSES.SAFERENT_ERROR ||
          request.status === SAFERENT_REQUEST_STATUSES.INTERNAL_ERROR
      )
    })
  } else if (buttonType === 'finalRequest') {
    const leadGroupUsers = rentalApplication.lead_group?.lead_group_users || []
    const finalRequests =
      rentalApplication.saferent_requests?.filter(
        (request) => request.type === SAFERENT_REQUEST_TYPES.FINAL
      ) || []

    // Check if any user has an error request
    canRerun = leadGroupUsers.some((lgu) => {
      const userRequests = finalRequests.filter((request) => request.user_id === lgu.users.id)
      if (userRequests.length === 0) {
        return true
      }
      // Check if user has at least one error request
      return userRequests.some(
        (request) =>
          request.status === SAFERENT_REQUEST_STATUSES.SAFERENT_ERROR ||
          request.status === SAFERENT_REQUEST_STATUSES.INTERNAL_ERROR
      )
    })
  }

  return (
    <ContentSectionCard
      title='Saferent Details'
      padding
      action={
        buttonType === 'initialRequest' && canRerun ? (
          <Button
            size='xs'
            colorScheme={'teal'}
            onClick={(e) => {
              e.stopPropagation()
              setRerunInitialRequestModalOpen(true)
            }}
          >
            Rerun Initial Underwriting Request
          </Button>
        ) : buttonType === 'edit' ? (
          <Button
            size='xs'
            colorScheme={'teal'}
            onClick={(e) => {
              e.stopPropagation()
              setEditModalOpen(true)
            }}
          >
            Edit
          </Button>
        ) : buttonType === 'finalRequest' && canRerun ? (
          <Button
            size='xs'
            colorScheme={'teal'}
            onClick={(e) => {
              e.stopPropagation()
              setRerunFinalRequestModalOpen(true)
            }}
          >
            Rerun Final Underwriting Request
          </Button>
        ) : undefined
      }
    >
      <SaferentDetailsInner rentalApplication={rentalApplication} />
      {rerunInitialRequestModalOpen && (
        <RereunRequestModal
          isOpen={rerunInitialRequestModalOpen}
          onClose={() => {
            onRefresh?.()
            setRerunInitialRequestModalOpen(false)
          }}
          rentalApplicationId={rentalApplication.id}
          rerunType='initial'
        />
      )}
      {rerunFinalRequestModalOpen && (
        <RereunRequestModal
          isOpen={rerunFinalRequestModalOpen}
          onClose={() => {
            onRefresh?.()
            setRerunFinalRequestModalOpen(false)
          }}
          rentalApplicationId={rentalApplication.id}
          rerunType='final'
        />
      )}
      {editModalOpen && (
        <EditSaferentDetailsModal
          isOpen={editModalOpen}
          onClose={() => {
            onRefresh?.()
            setEditModalOpen(false)
          }}
          rentalApplication={rentalApplication}
        />
      )}
    </ContentSectionCard>
  )
}

export default SaferentDetails
